import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { HttpClient } from '@angular/common/http';

/** One agent-proposed action waiting on a human, as `/api/approvals` sends it. */
export interface ApprovalRow {
  id: number;
  requestType: string;
  objectType: string | null;
  objectId: number | null;
  payload: unknown;
  payloadHash: string;
  riskLevel: string;
  summary: string | null;
  status: string;
  requestedByActorType: string;
  agentJobId: number | null;
  expiresAt: string | null;
  createdAt: string;
}

/**
 * Pending approvals, on the page the record lives on.
 *
 * There used to be an Approvals page of its own, reached from the Dashboard. A
 * decision about a lead is a decision about a lead: it belongs where the lead
 * is, not on a screen a person has to remember to visit. So this is a strip the
 * boards put above their work — closed it is one line saying how many are
 * waiting, open it is the same list the page had, and the decision is made
 * without leaving.
 *
 * `objectType` scopes it, so the roll-off board is never told about a lead's
 * approvals and the lead board is never told about a hire's. A board whose
 * kind has nothing pending renders nothing at all.
 */
@Component({
  selector: 'app-approvals-panel',
  templateUrl: './approvals-panel.component.html',
  styleUrls: ['./approvals-panel.component.css'],
})
export class ApprovalsPanelComponent implements OnInit {
  /** Which records this board is about. Null shows every pending approval. */
  @Input() objectType: string | null = null;
  /** Emitted after a decision lands, so the host can reload what it draws. */
  @Output() decided = new EventEmitter<void>();
  /** The pending set, for a host that also marks its own cards. */
  @Output() pendingChanged = new EventEmitter<ApprovalRow[]>();

  rows: ApprovalRow[] = [];
  open = false;
  loadFailed = false;
  expanded: number | null = null;
  reason = '';
  deciding: number | null = null;
  decideError: string | null = null;

  constructor(private http: HttpClient) {}

  ngOnInit(): void { this.load(); }

  load(): void {
    this.http.get<ApprovalRow[]>('/api/approvals', { params: { status: 'Pending' } })
      .subscribe({
        next: rows => {
          this.loadFailed = false;
          this.rows = this.objectType
            ? rows.filter(row => row.objectType === this.objectType)
            : rows;
          this.pendingChanged.emit(this.rows);
        },
        // An account without Automation.Approve gets a 403 here, which is not
        // an error to report: it means this person does not approve things, and
        // the strip simply is not for them.
        error: () => {
          this.loadFailed = true;
          this.rows = [];
          this.pendingChanged.emit([]);
        },
      });
  }

  toggle(): void {
    this.open = !this.open;
    this.expanded = null;
    this.decideError = null;
  }

  expand(row: ApprovalRow): void {
    this.expanded = this.expanded === row.id ? null : row.id;
    this.reason = '';
    this.decideError = null;
  }

  /** The payload verbatim: what is approved is exactly what executes, and it
   *  is hash-bound, so it is shown rather than summarised. */
  payloadText(row: ApprovalRow): string {
    return JSON.stringify(row.payload, null, 2);
  }

  decide(row: ApprovalRow, approve: boolean): void {
    if (this.deciding != null) return;
    this.deciding = row.id;
    this.decideError = null;
    this.http.post(`/api/approvals/${row.id}/decide`, { approve, reason: this.reason })
      .subscribe({
        next: () => {
          this.deciding = null;
          this.expanded = null;
          this.load();
          this.decided.emit();
        },
        error: err => {
          this.deciding = null;
          this.decideError = err.error?.error ?? 'The decision was not recorded.';
        },
      });
  }
}
